/**
 * The finance module: live state from state.ext.finance (kept current by SSE), the heavier
 * report fetched from GET /api/finance/report and refetched when the state's version moves,
 * and every change sent to the hub (or to the demo store in mock mode).
 */
import type { CostEntry, CostEntryInput, FinanceReport, FinanceSettingsInput, FinanceState, Id, Receipt, SubscriptionInput, SyncKind } from '@orgus/protocol';
import { useEffect, useRef, useState } from 'react';
import { API_BASE, ApiError, api, toUiError } from '../../lib/api.ts';
import type { UiError } from '../../lib/api.ts';
import { useOrgus, useExt } from '../../lib/store.ts';
import { useCosts } from '../costs/useCosts.ts';
import { buildMockReport, useMockFinance } from './mock.ts';
import { CSRF_HEADER } from '@orgus/protocol';

const isMock = () => useOrgus.getState().mode === 'mock';

export function useFinance(): FinanceState | undefined {
  const mode = useOrgus((s) => s.mode);
  const live = useExt<FinanceState>('finance');
  const mock = useMockFinance((s) => s.state);
  return mode === 'mock' ? mock : live;
}

export interface ReportQuery {
  from: string;
  to: string;
  projectId: Id | null;
  /** An account key or 'unknown' (ADR 0067); absent or null for every account. */
  account?: string | null;
}

export interface ReportResult {
  report: FinanceReport | null;
  /** First load for this query. */
  loading: boolean;
  /** A newer report is being fetched while the old one shows. */
  refreshing: boolean;
  error: UiError | null;
  retry(): void;
}

/** The report for a range and project; refetched when finance state changes or every minute. */
export function useFinanceReport(q: ReportQuery): ReportResult {
  const mode = useOrgus((s) => s.mode);
  const finance = useFinance();
  const costs = useCosts();
  const projects = useOrgus((s) => s.projects);
  const [state, setState] = useState<{ key: string; report: FinanceReport | null; error: UiError | null; loading: boolean }>({ key: '', report: null, error: null, loading: true });
  const [nonce, setNonce] = useState(0);
  const [tick, setTick] = useState(0);
  const account = q.account ?? null;
  const key = `${q.from}|${q.to}|${q.projectId ?? ''}|${account ?? ''}`;
  const version = finance?.version ?? 0;
  const keyRef = useRef(key);
  keyRef.current = key;

  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (mode === 'mock') return;
    const ctrl = new AbortController();
    setState((s) => (s.key === key ? { ...s, loading: !s.report } : { key, report: null, error: null, loading: true }));
    const params = new URLSearchParams({ from: q.from, to: q.to });
    if (q.projectId) params.set('project', q.projectId);
    if (account) params.set('account', account);
    api<FinanceReport>(`/api/finance/report?${params.toString()}`, { signal: ctrl.signal })
      .then((report) => {
        if (keyRef.current === key) setState({ key, report, error: null, loading: false });
      })
      .catch((e) => {
        if (ctrl.signal.aborted) return;
        setState((s) => ({ ...s, key, error: toUiError(e, { what: 'The finance report did not load.' }), loading: false }));
      });
    return () => ctrl.abort();
  }, [mode, key, version, nonce, tick, q.from, q.to, q.projectId, account]);

  if (mode === 'mock') {
    const report = costs && finance ? buildMockReport({ costs, finance, from: q.from, to: q.to, projectId: q.projectId, account, projects, now: Date.now() }) : null;
    return { report, loading: !report, refreshing: false, error: null, retry: () => undefined };
  }
  const same = state.key === key;
  return {
    report: same ? state.report : null,
    loading: !same || (state.loading && !state.report),
    refreshing: same && state.loading && !!state.report,
    error: same ? state.error : null,
    retry: () => setNonce((n) => n + 1),
  };
}

function apply(s: FinanceState | undefined): void {
  if (s) useOrgus.getState().applyExt({ ext: 'finance', type: 'state', data: s });
}

/** Create or replace an entry; resolves with the saved entry (to attach a receipt to it). */
export async function saveEntry(input: CostEntryInput, id?: Id): Promise<CostEntry> {
  if (isMock()) return useMockFinance.getState().saveEntry(input, id);
  const res = id
    ? await api<{ entry: CostEntry; state: FinanceState }>(`/api/finance/entries/${encodeURIComponent(id)}`, { method: 'PUT', body: input })
    : await api<{ entry: CostEntry; state: FinanceState }>('/api/finance/entries', { method: 'POST', body: input });
  apply(res.state);
  return res.entry;
}

export async function deleteEntry(id: Id): Promise<void> {
  if (isMock()) return useMockFinance.getState().removeEntry(id);
  apply((await api<{ state: FinanceState }>(`/api/finance/entries/${encodeURIComponent(id)}`, { method: 'DELETE' })).state);
}

export const RECEIPT_ACCEPT = 'application/pdf,image/png,image/jpeg,image/webp';
export const RECEIPT_MAX_BYTES = 5 * 1024 * 1024;

/** Attach a receipt (raw upload, not JSON). */
export async function uploadReceipt(entry: CostEntry, file: File): Promise<void> {
  if (!RECEIPT_ACCEPT.split(',').includes(file.type)) {
    throw new ApiError({ what: 'That receipt was not attached.', why: 'Receipts must be a PDF, PNG, JPEG or WebP file.', next: 'Save the receipt as a PDF or an image and attach it again.' });
  }
  if (file.size > RECEIPT_MAX_BYTES) {
    throw new ApiError({ what: 'That receipt is too large.', why: 'Receipts can be at most 5 MB.', next: 'Upload a smaller scan or a PDF.' });
  }
  if (isMock()) {
    const receipt: Receipt = { name: file.name, type: file.type, size: file.size, uploadedAt: Date.now() };
    return useMockFinance.getState().setReceipt(entry.id, receipt);
  }
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/api/finance/entries/${encodeURIComponent(entry.id)}/receipt`, {
      method: 'PUT',
      credentials: 'same-origin',
      headers: { 'Content-Type': file.type, [CSRF_HEADER]: '1', 'X-File-Name': encodeURIComponent(file.name) },
      body: file,
    });
  } catch {
    throw new ApiError({ what: 'Could not reach the hub.', why: 'The upload failed before the hub answered.', next: 'Check that Orgus is running, then attach the receipt again.' });
  }
  const json = (await res.json().catch(() => undefined)) as { state?: FinanceState; error?: UiError } | undefined;
  if (!res.ok) throw new ApiError(json?.error ?? { what: 'That receipt was not attached.', why: `The hub answered with status ${res.status}.`, next: 'Try again.' }, res.status);
  apply(json?.state);
}

export async function removeReceipt(id: Id): Promise<void> {
  if (isMock()) return useMockFinance.getState().setReceipt(id, undefined);
  apply((await api<{ state: FinanceState }>(`/api/finance/entries/${encodeURIComponent(id)}/receipt`, { method: 'DELETE' })).state);
}

export function receiptUrl(id: Id, download = false): string {
  return `/api/finance/entries/${encodeURIComponent(id)}/receipt${download ? '?download=1' : ''}`;
}

/** Import invoice rows from CSV text; resolves with how many were added. */
export async function importInvoices(csv: string, preview: CostEntry[]): Promise<number> {
  if (isMock()) {
    useMockFinance.getState().importRows(preview);
    return preview.length;
  }
  const res = await api<{ added: number; state: FinanceState }>('/api/finance/import', { method: 'POST', body: { csv } });
  apply(res.state);
  return res.added;
}

export async function saveSubscription(input: SubscriptionInput, id?: Id): Promise<void> {
  if (isMock()) return useMockFinance.getState().saveSubscription(input, id);
  const res = id
    ? await api<{ state: FinanceState }>(`/api/finance/subscriptions/${encodeURIComponent(id)}`, { method: 'PUT', body: input })
    : await api<{ state: FinanceState }>('/api/finance/subscriptions', { method: 'POST', body: input });
  apply(res.state);
}

export async function deleteSubscription(id: Id): Promise<void> {
  if (isMock()) return useMockFinance.getState().removeSubscription(id);
  apply((await api<{ state: FinanceState }>(`/api/finance/subscriptions/${encodeURIComponent(id)}`, { method: 'DELETE' })).state);
}

export async function saveFinanceSettings(input: FinanceSettingsInput): Promise<void> {
  if (isMock()) return useMockFinance.getState().saveSettings(input);
  apply(await api<FinanceState>('/api/finance/settings', { method: 'PUT', body: input }));
}

/** Pull billing or GitHub now. Pulls can take a while: the timeout is generous. */
export async function syncNow(kind: SyncKind): Promise<void> {
  if (isMock()) {
    await new Promise((r) => setTimeout(r, 600));
    return useMockFinance.getState().synced(kind);
  }
  apply(await api<FinanceState>(`/api/finance/sync/${kind}`, { method: 'POST', timeoutMs: 120_000 }));
}
